import styles from "./AtmosphericPanel.module.css";

export type AuthView =
  | "signin"
  | "signup"
  | "signup-verify"
  | "signup-waitlist"
  | "reset-request"
  | "reset-sent"
  | "reset-confirm";

interface PanelContent {
  kicker: string;
  headline: string;
  body: string;
}

const CONTENT: Record<AuthView, PanelContent> = {
  signin: {
    kicker: "Welcome back",
    headline: "Your queue is waiting.",
    body: "Pick up wherever you left off — drafts, calendar, and the approval queue are one tap away.",
  },
  signup: {
    kicker: "Join the waitlist",
    headline: "Learns your brand once. Drafts every day.",
    body: "We’re onboarding new workspaces in waves. Tell us about yours and we’ll be in touch.",
  },
  "signup-verify": {
    kicker: "Verify your email",
    headline: "Check your inbox.",
    body: "We just emailed a 6-digit code. It expires in 30 minutes. Pop it into the boxes on the right.",
  },
  "signup-waitlist": {
    kicker: "You’re on the list",
    headline: "Thanks — we’ll be in touch.",
    body: "We open access in small waves so every new workspace gets a real onboarding. Watch your inbox.",
  },
  "reset-request": {
    kicker: "Account recovery",
    headline: "Let’s get you back in.",
    body: "We’ll send a 6-digit code to your email. Codes expire in 30 minutes for security.",
  },
  "reset-sent": {
    kicker: "Account recovery",
    headline: "Check your inbox.",
    body: "We sent a 6-digit code to the email you gave us. If it doesn’t arrive in 5 minutes, check spam or request a new one.",
  },
  "reset-confirm": {
    kicker: "Account recovery",
    headline: "Set a new password.",
    body: "Choose something memorable. Sessions on other devices will be signed out automatically.",
  },
};

interface AtmosphericPanelProps {
  view: AuthView;
}

/**
 * Decorative left panel: kicker, headline, body copy that morphs per
 * view, plus a draft-preview card on the sign-in/sign-up screens. The
 * verify + reset-sent screens deliberately render NO mock email card —
 * an example "verification code" alongside the real form reads as
 * either the user's actual code (causing confusion) or as a security
 * leak (looks like we're exposing codes in the UI). Plain copy only.
 */
export function AtmosphericPanel({ view }: AtmosphericPanelProps) {
  const content = CONTENT[view];
  const showDraftPreview = view === "signin" || view === "signup";

  return (
    <aside className={styles.panel}>
      <div className={styles.texture} aria-hidden="true" />
      <div className={styles.intro}>
        <span className={styles.kicker}>{content.kicker}</span>
        <h1 className={styles.headline}>{content.headline}</h1>
        <p className={styles.body}>{content.body}</p>
      </div>

      <div className={styles.grow} />

      {showDraftPreview && <DraftPreview />}

      <footer className={styles.footer}>
        <span>socialmediamanager.pheonixapps.com</span>
        <div className={styles.grow} />
        <a className={styles.footLink} href="/privacy">
          Privacy
        </a>
        <a className={styles.footLink} href="/terms">
          Terms
        </a>
      </footer>
    </aside>
  );
}

function DraftPreview() {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.platformDot} style={{ background: "var(--platform-ig)" }} />
        <span className={styles.cardMeta}>Carousel · 5 slides</span>
        <div className={styles.grow} />
        <span className={styles.pill} data-state="approved">
          Approved
        </span>
      </div>
      <div className={styles.cardBody}>
        Every crew has one. The patch-pocket, hydration-pusher, lost-friend-finder.
      </div>
      <div className={styles.cardFoot}>
        <span>Sample draft · your workspace will look like this</span>
      </div>
    </div>
  );
}
